import { Variant } from "@flypengine/myFlyp-lib"
import * as O from "fp-ts/Option"
import * as E from "fp-ts/Either"
import * as Firebase from "firebase/auth"
import { AuthStatus } from "../Authorization"

export type Config<msg> = {
  wrapperMsg: (m: Msg) => msg
}

export type Model = {
  loginDetails: LoginModel
  logged: E.Either<AuthStatus, Firebase.User>
}

export type LoginModel = {
  title: string
  description: string
  email: O.Option<string>
  error: O.Option<string>
  callToAction: string
}

export const _init = "init"
export const _emailChanged = "emailChanged"
export const _loginEmailReq = "loginEmailReq"
export const _loginEmailRes = "loginEmailRes"
export const _loginReqMsg = "loginReqMsg"
export const _loginResMsg = "loginResMsg"
export const _verifyLoginEmailReq = "verifyLoginEmailReq"
export const _verifyLoginEmailRes = "verifyLoginEmailRes"

export type Msg =
  | Variant.Tagged<typeof _init, AuthStatus>
  | Variant.Tagged<typeof _emailChanged, O.Option<string>>
  | Variant.Tagged<typeof _loginEmailReq, void>
  | Variant.Tagged<typeof _loginEmailRes, E.Either<AuthStatus, Firebase.User>>
  | Variant.Tagged<typeof _loginReqMsg, void>
  | Variant.Tagged<typeof _loginResMsg, E.Either<AuthStatus, void>>
  | Variant.Tagged<typeof _verifyLoginEmailReq, void>
  | Variant.Tagged<
      typeof _verifyLoginEmailRes,
      E.Either<AuthStatus, Firebase.User>
    >

export const Msg = Variant.module<Msg>({
  init: value => Variant.tagged(_init, value),
  emailChanged: value => Variant.tagged(_emailChanged, value),
  loginEmailReq: value => Variant.tagged(_loginEmailReq, value),
  loginEmailRes: value => Variant.tagged(_loginEmailRes, value),
  loginReqMsg: value => Variant.tagged(_loginReqMsg, value),
  loginResMsg: value => Variant.tagged(_loginResMsg, value),
  verifyLoginEmailReq: value => Variant.tagged(_verifyLoginEmailReq, value),
  verifyLoginEmailRes: value => Variant.tagged(_verifyLoginEmailRes, value),
})

export const initLoginModel = (authStatus: AuthStatus): LoginModel => {
  switch (authStatus) {
    case "Login Link Failed":
    case "Login Failed": {
      return {
        title: "Something went wrong...",
        description: "There was an error signing you in. Please try again",
        email: O.none,
        callToAction: "Send me another login link",
        error: O.none,
      }
    }

    case "Login Failed Credentials": {
      return {
        title: "Verify that it's you",
        description: "Enter the email address you used for sign in",
        email: O.none,
        error: O.none,
        callToAction: "Continue",
      }
    }

    case "Error": {
      return {
        title: "Something went wrong...",
        description:
          "There was an error signing you in. Please make sure your browser is not using Incognito mode and try again",
        email: O.none,
        callToAction: "Send me another login link",
        error: O.none,
      }
    }

    default: {
      return {
        email: O.none,
        error: O.none,
        title: "Welcome to your marketplace",
        description: "Enter your email to get started",
        callToAction: "Send me a login link",
      }
    }
  }
}
